import React, { useState } from 'react'
import { AdminPanelPage } from '../AdminPanelPage'
import { BreadcrumbProps, Button, Heading, Icon, Input, Modal, Tooltip, useToast } from '@aidkitorg/component-library'
import { DashboardBuilderPanel } from './components/DashboardBuilderPanel'
import { useLocalizedStrings } from '../../Localization'
import { useDashboards } from '../../Contexts/DashboardsContext'
import { DashboardComponentCard } from './components/DashboardComponents/DashboardComponentCard'
import { useHistory } from 'react-router-dom'

export const DashboardBuilderPage = () => {
  const L = useLocalizedStrings();

  const { stagedComponents, stagedComponentsWithResults, setStagedComponents, createDashboard } = useDashboards()
  const { toast } = useToast()
  const history = useHistory()

  const [title, setTitle] = useState('My Dashboard')
  const [description, setDescription] = useState('A customizable dashboard for data visualization')
  const [isEditingHeader, setIsEditingHeader] = useState(false)

  // TODO: update this to default based on whether this is a new or existing dashboard
  const [dataSourceModalOpen, setDataSourceModalOpen] = useState(true)

  const Header = () => {
    const [tempTitle, setTempTitle] =useState(title)
    const [tempDescription, setTempDescription] = useState(description)

    return (
      <div className="w-full flex justify-between">
        <div>
          {!isEditingHeader && (<>
            <Heading level="h1" className="m-0">{title}</Heading>
            <div className="inline-block text-lg text-gray-700">
              {description}
            </div>
          </>)}
          {isEditingHeader && (
            <div className="flex flex-col gap-y-2">
              <Input
                className="text-5xl font-bold"
                label="Dashboard Title"
                showLabel={false} 
                value={tempTitle} 
                onChange={(e) => setTempTitle(e.target.value)}
              />
              <Input
                className="text-lg"
                label="Dashboard Description"
                showLabel={false} 
                value={tempDescription} 
                onChange={(e) => setTempDescription(e.target.value)}
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isEditingHeader && (
            <>
              <Button variant="secondary" onClick={() => setIsEditingHeader(true)}>Edit</Button>
              <Button variant="secondary" onClick={() => setDataSourceModalOpen(true)}>Data Source</Button>
              <Button variant="primary" onClick={() => {
                createDashboard(title, description, stagedComponents)
              }}>Save Dashboard</Button>
            </>
          )}
          {isEditingHeader && (
            <>
              <Button variant="secondary" onClick={() => {
                setTempTitle(title)
                setTempDescription(description)
                setIsEditingHeader(false)
              }}>Cancel</Button>
              <Button variant="primary" onClick={() => {
                setTitle(tempTitle)
                setDescription(tempDescription)
                setIsEditingHeader(false)
              }}>Save Changes</Button>
            </>
          )}
        </div>
      </div>
    )
  }

  const pageActionButtons = [
    <Tooltip text={"Select data source"} asChild>
      <Button
        icon={<Icon role="decorative" type="solid" name="CircleStackIcon" />}
        variant="secondary"
        size="sm"
        aria-label={"Select data source"}
        onClick={() => { setDataSourceModalOpen(true) }}
      />
    </Tooltip>,    
    <Button variant="primary" onClick={async () => {
      const createdDashboard = await createDashboard(title, description, stagedComponents)
      if (createdDashboard) {
        setStagedComponents([])
        toast({
          title: 'Success',
          description: 'Dashboard successfully created',
          variant: 'success'
        })
        
        history.push("/admin-panel/my-dashboards/"+ createdDashboard)
      } else {
        toast({
          title: 'Error',
          description: 'Error creating dashboard',
          variant: 'error'
        })
      }
    }}>
      Create Dashboard
    </Button>
  ]
  
  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: 'Dashboards',
      href: '/admin-panel/my-dashboards'
    },
    {
      label: 'Dashboard Builder',
    }
  ]

  const DataSourceModal = () => {
    return (
      <Modal size='lg' title="Select Report" open={dataSourceModalOpen} onClose={() => setDataSourceModalOpen(false)}>
        {/* TODO: add in reports, for now just have applications */}
        <div className="text-sm text-gray-700 -mt-2 mb-4">Choose a report to create visualizations from its data.</div>
        <div className="flex flex-col gap-y-4">
          <div className="uppercase text-sm text-gray-600 font-medium tracking-wider">Applications</div>
          <Button
            variant="secondary"
            className='!w-full !justify-start'
            // TODO: update this so that it actually changes the data source
            onClick={() => setDataSourceModalOpen(false)}
          >
            <div className="flex items-center gap-x-4 py-2">
              <div>
                <Icon strokeWidth={2} className="text-blue-700" name="DocumentTextIcon" role="decorative"/>
              </div>
              <div className="text-left flex flex-col gap-y-1">
                <div>Application Statistics</div>
                <div className="text-gray-700 font-normal">
                  Key metrics and trends from application data
                </div>
              </div>
            </div>
          </Button>
        </div>
      </Modal>
    )
  }
  // TODO: add modal and report selection, for now, just build from an empty page
  
  return (
    <AdminPanelPage
      title={L.admin_panel.dashboards.dashboards}
      description={"A customizable dashboard for data visualization"}
      pageActionButtons={pageActionButtons}
      breadcrumbs={breadcrumbs}
    >
      <div className="flex justify-between gap-4">
        <div>
          <div className="flex flex-row flex-wrap gap-2">
            {
              stagedComponentsWithResults.map((component, idx) => {
                return (
                  <div className="flex-none">
                    <DashboardComponentCard key={idx} component={component} />
                  </div>
                )
              })
            }
          </div>
        </div>
        <div className="flex-shrink-0 w-80 h-full border rounded-md bg-white">
          <DashboardBuilderPanel/>
        </div>
        <DataSourceModal/>
      </div>
    </AdminPanelPage>
  )
}